/**
 * iCal Event Generation - Shared Utility
 * 
 * Generate iCal files for calendar events.
 * Single source of truth to avoid duplicate implementations.
 */

import type { Event } from '@/lib/types';

const SITE_NAME = 'Auto News Weekly';
const SITE_DOMAIN = 'autonewsweekly.com';

/**
 * Format a date for iCal format (YYYYMMDD or YYYYMMDDTHHMMSSZ)
 */
function formatICalDate(date: Date, allDay = false): string {
  if (allDay) {
    return date.toISOString().split('T')[0].replace(/-/g, '');
  }
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

/**
 * Escape special characters for iCal text fields
 */
function escapeICalText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Generate an iCal file content for an event
 */
export function generateICalContent(event: Event): string {
  const startDate = new Date(event.event_date);
  const endDate = event.end_date
    ? new Date(event.end_date)
    : new Date(startDate.getTime() + 24 * 60 * 60 * 1000); // Default to 1 day

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${SITE_NAME}//Event//EN`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@${SITE_DOMAIN}`,
    `DTSTAMP:${formatICalDate(new Date())}`,
    `DTSTART:${formatICalDate(startDate, true)}`,
    `DTEND:${formatICalDate(endDate, true)}`,
    `SUMMARY:${escapeICalText(event.title)}`,
  ];

  if (event.location) {
    lines.push(`LOCATION:${escapeICalText(event.location)}`);
  }

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeICalText(event.description)}`);
  }

  if (event.external_link) {
    lines.push(`URL:${event.external_link}`);
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');

  return lines.join('\r\n');
}

/**
 * Download an iCal file for an event
 */
export function downloadICalFile(event: Event): void {
  const content = generateICalContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.slug || event.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Generate a Google Calendar URL for an event
 */
export function generateGoogleCalendarUrl(event: Event): string {
  const startDate = new Date(event.event_date);
  const endDate = event.end_date
    ? new Date(event.end_date)
    : new Date(startDate.getTime() + 24 * 60 * 60 * 1000);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatICalDate(startDate)}/${formatICalDate(endDate)}`,
  });

  if (event.location) {
    params.set('location', event.location);
  }

  if (event.description) {
    params.set('details', event.description);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
